import React, { useCallback, useContext, useEffect, useState } from 'react';
import useStaticCallback from 'hooks/useStaticCallback';

import {
  UnitPlaybackManagerContextType,
  UnitPlaybackManagerProviderProps,
} from './types';

export const UnitPlaybackManagerContext = React.createContext<
  UnitPlaybackManagerContextType | undefined
>(undefined);

export const UnitPlaybackManagerProvider: React.FC<UnitPlaybackManagerProviderProps> = ({
  children,
}) => {
  const [playingUnitId, setPlayingUnitId] = useState<string>();

  const pause = useCallback(() => setPlayingUnitId(undefined), []);
  const play = useCallback((unitId: string) => setPlayingUnitId(unitId), []);

  return (
    <UnitPlaybackManagerContext.Provider value={{ pause, play, playingUnitId }}>
      {children}
    </UnitPlaybackManagerContext.Provider>
  );
};

export function useUnitPlaybackManager(unitId?: string, autoplay?: boolean) {
  const context = useContext(UnitPlaybackManagerContext);

  if (context === undefined) {
    throw new Error(
      'useUnitPlaybackManager must be used within UnitPlaybackManagerProvider',
    );
  }

  const { play, playingUnitId, pause, ...rest } = context;

  const playUnit = useStaticCallback(() => {
    if (unitId) {
      play(unitId);
    }
  });

  useEffect(() => {
    if (autoplay) {
      playUnit();
    }
  }, [autoplay, playUnit, unitId]);

  return {
    ...rest,
    pause,
    play: playUnit,
    playingUnitId,
  };
}
